import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Kunde } from '../types';
import kundeService from '../services/kundeService';
import { Icon, Spinner, inputClass } from './ui';

interface KundeSelectProps {
  /** Aktuell gewählte Kunden-ID (0/undefined = keiner). */
  value?: number;
  /** Wird mit der neuen ID aufgerufen (undefined, wenn Auswahl geleert). */
  onChange: (id: number | undefined) => void;
  /** Optionale vorgeladene Liste, um das Label der aktuellen Auswahl ohne Request aufzulösen. */
  kunden?: Kunde[];
  placeholder?: string;
  /** Erlaubt das Leeren der Auswahl (x-Button + „kein Kunde“). */
  allowClear?: boolean;
  autoFocus?: boolean;
}

const label = (k: Kunde) => `${k.kundennummer} – ${k.vorname} ${k.nachname}`;

/**
 * Typeahead-Auswahl für Kunden: serverseitige Suche (`/kunden/search`) mit Debounce,
 * ersetzt das einfache Dropdown, das bei vielen Kunden unbrauchbar wird.
 */
const KundeSelect: React.FC<KundeSelectProps> = ({ value, onChange, kunden = [], placeholder = 'Kunde suchen…', allowClear = true, autoFocus = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Kunde[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Kunde | null>(null);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  // Label der aktuellen Auswahl auflösen: erst aus der vorgeladenen Liste, sonst per Einzel-Request.
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    if (selected?.id === value) return;
    const local = kunden.find(k => k.id === value);
    if (local) { setSelected(local); return; }
    kundeService.getById(value).then(setSelected).catch(() => setSelected(null));
  }, [value, kunden]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced Suche.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) {
      // Ohne Eingabe die ersten Einträge der vorgeladenen Liste zeigen (falls vorhanden).
      setResults(kunden.slice(0, 20));
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      kundeService.search(q)
        .then(rs => { setResults(rs); setActive(0); })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query, open, kunden]);

  // Klick außerhalb schließt das Dropdown.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = useCallback((k: Kunde) => {
    setSelected(k);
    onChange(k.id);
    setOpen(false);
    setQuery('');
  }, [onChange]);

  const clear = () => { setSelected(null); onChange(undefined); setQuery(''); };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (results[active]) pick(results[active]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={boxRef} className="relative">
      {selected && !open ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
          <button type="button" className="flex-1 text-left text-gray-900" onClick={() => { setOpen(true); setQuery(''); }}>
            {label(selected)}
          </button>
          <div className="flex items-center gap-1">
            <button type="button" className="rounded p-1 text-gray-400 hover:text-gray-600" title="Ändern" onClick={() => { setOpen(true); setQuery(''); }}>
              <Icon.Search className="h-4 w-4" />
            </button>
            {allowClear && (
              <button type="button" className="rounded p-1 text-gray-400 hover:text-red-600" title="Auswahl entfernen" onClick={clear}>
                <Icon.Close className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="relative">
          <Icon.Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            className={`${inputClass} pl-9`}
            value={query}
            placeholder={placeholder}
            autoFocus={autoFocus || open}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
          />
        </div>
      )}

      {open && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-gray-500"><Spinner className="h-4 w-4" /> Suche…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500">{query.trim() ? 'Keine Kunden gefunden' : 'Tippen, um zu suchen'}</div>
          ) : (
            results.map((k, i) => (
              <button
                type="button"
                key={k.id}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(k)}
                className={`flex w-full flex-col items-start px-3 py-2 text-left text-sm ${i === active ? 'bg-brand-50' : 'hover:bg-gray-50'} ${k.id === value ? 'font-semibold text-brand-700' : 'text-gray-800'}`}
              >
                <span>{k.vorname} {k.nachname}</span>
                <span className="text-xs text-gray-500">{k.kundennummer}{k.ort ? ` · ${k.ort}` : ''}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default KundeSelect;
